import { useRef, useState } from 'react'
import { Camera, Loader2, Trash2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/components'
import { PhotosApi } from '@/shared/api'
import { createApiClient } from '@/shared/api/client'
import { cn } from '@/shared/utils'
import { getTripCoverUrl } from '@/shared/constants'

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ACCEPT_TYPES = 'image/jpeg,image/png,image/webp,image/heic'

interface TripCoverDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tripId: string
  coverPhotoUrl: string | null
  onCoverUpdate: () => void
  getAccessToken: () => Promise<string | null>
}

export function TripCoverDialog({
  open,
  onOpenChange,
  tripId,
  coverPhotoUrl,
  onCoverUpdate,
  getAccessToken
}: TripCoverDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isRemoving, setIsRemoving] = useState(false)

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      return
    }

    setIsUploading(true)
    try {
      const photosApi = new PhotosApi(getAccessToken)
      const uploadResponse = await photosApi.createPhoto(tripId, {
        photo: file,
        caption: 'Cover photo'
      })
      const photo = uploadResponse?.photo ?? uploadResponse
      if (!photo?.id) {
        throw new Error('Invalid photo response from server')
      }

      const apiClient = createApiClient(getAccessToken)
      await apiClient.put(`/trips/${tripId}/cover-photo/${photo.id}`)

      await onCoverUpdate()
      onOpenChange(false)
    } catch (err) {
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleRemoveCover = async () => {
    setIsRemoving(true)
    try {
      const apiClient = createApiClient(getAccessToken)
      await apiClient.delete(`/trips/${tripId}/cover-photo`)
      await onCoverUpdate()
      onOpenChange(false)
    } catch (err) {
    } finally {
      setIsRemoving(false)
    }
  }

  const busy = isUploading || isRemoving

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Cover photo</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div
            className={cn(
              'relative w-full aspect-video overflow-hidden rounded-lg bg-adventure-200 flex items-center justify-center'
            )}
          >
            <img
              src={getTripCoverUrl(coverPhotoUrl)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
              className="gap-2"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
              {coverPhotoUrl ? 'Change image' : 'Upload image'}
            </Button>
            {coverPhotoUrl && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={handleRemoveCover}
                className="gap-2 text-destructive border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
              >
                {isRemoving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Remove cover
              </Button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_TYPES}
            onChange={handleFileSelect}
            className="hidden"
            aria-label="Upload trip cover image"
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
